/* PhoneGate — «спросить телефон один раз перед оплатой».
 *
 * Почему отдельным ШАГОМ, а не полем в каждой форме оплаты: платёжных форм в
 * продукте пять (тариф подписки, визард бизнес-пространства, оплата по именной
 * ссылке, счёт для юрлиц, покупка курса), у них разные payload'ы и разные
 * бэкенд-контракты. Поле пришлось бы завести и провалидировать пятикратно, и
 * пятая копия рано или поздно разошлась бы с остальными. Шаг перед оплатой
 * ничего не меняет в платёжных запросах: телефон сохраняется профилем, а
 * оплата уходит ровно тем же вызовом, что и раньше.
 *
 * Кого не спрашиваем: у кого телефон уже есть. Это правило живёт и на бэке
 * (accounts.phones.ensure_phone_for_payment) — фронт лишь избавляет от лишнего
 * шага, а гарантию даёт сервер.
 *
 * Кнопка называется просто «Продолжить», а не «Продолжить к оплате». Шаг с
 * 26.08.2026 стоит и на БЕСПЛАТНОМ «Быстром старте», где оплаты нет вовсе, и
 * прежняя подпись обещала списание там, где его не будет. Формулировка обязана
 * оставаться верной во всех пяти входах разом — иначе придётся заводить пятую
 * копию подписи, а она рано или поздно разойдётся с остальными.
 *
 * Действие за гейтом выполняется РОВНО ОДИН раз. За ним стоят необратимые вещи
 * (создание бизнес-пространства, checkout, счёт), а два Enter подряд приходят в
 * одну перерисовку: `busy` в замыкании ещё false, и оба нажатия запускали своё
 * сохранение — и своё действие. Защита в двух местах: save не входит повторно
 * (ref, а не состояние), а хук отдаёт действие на исполнение одним движением
 * «забрать и выполнить» — второй onSaved найдёт пустое место.
 */
import React from 'react';
import { Modal } from '../ui/Modal.jsx';
import { Button } from '../ui/Button.jsx';
import { Field, TextInput } from '../ui/Field.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { profileReal } from '../../lib/api/index.js';

const { useState, useCallback, useRef } = React;

// Формат номера объясняем ОДНОЙ фразой на весь продукт: вторая копия неизбежно
// разойдётся с первой, и один и тот же ввод будет объясняться по-разному в
// оплате и в поддержке. Импортируется формой обращения в поддержку.
export const PHONE_HINT = 'Российский можно без кода страны; для остальных '
  + 'укажите код, например +33 6 12 34 56 78';
export const PHONE_PLACEHOLDER = '+7 999 123-45-67';

export function PhoneGateModal({ open, onClose, onSaved, busy: outerBusy }) {
  const { refreshUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Идёт ли сохранение — то же, что `busy`, но читается сразу, а не со следующей
  // перерисовки: второй вызов из того же замыкания (второй Enter, клик вдогонку)
  // состояния ещё не видит.
  const savingRef = useRef(false);
  // Enter — та же кнопка «Продолжить»: когда она заблокирована, молчит и клавиша.
  // Автоповтор зажатой клавиши (e.repeat) — не второе решение человека: пропускаем.
  const blocked = !phone.trim() || busy || !!outerBusy;

  const save = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setBusy(true); setErr('');
    try {
      await profileReal.updateProfile({ phone });
      // Профиль обновлён — перечитываем пользователя, иначе следующая оплата
      // спросит телефон повторно (в контексте он ещё пустой).
      await refreshUser();
      onSaved && onSaved(phone);
    } catch (e) {
      // Текст отказа приходит с бэка готовым к показу (accounts/phones.py):
      // «укажите номер с кодом страны» и т.п. Свой словарь тут не заводим,
      // иначе он разойдётся с серверным.
      const d = e && (e.phone && e.phone[0] || e.detail || e.message);
      setErr(d || 'Проверьте номер телефона.');
    } finally { savingRef.current = false; setBusy(false); }
  }, [phone, refreshUser, onSaved]);

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Номер телефона">
      <div className="stack stack-4">
        <p className="t-body">
          Оставьте номер — он нужен, чтобы связаться с вами по оплате и доступу.
          Спрашиваем один раз: дальше он подставится сам.
        </p>
        <Field label="Телефон" error={err} hint={PHONE_HINT}>
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder={PHONE_PLACEHOLDER} inputMode="tel" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.repeat && !blocked) save(); }} />
        </Field>
        <div className="cluster cluster-2" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose} disabled={busy || outerBusy}>Отмена</Button>
          <Button variant="primary" onClick={save} disabled={blocked}>
            {busy ? 'Сохраняем…' : 'Продолжить'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Обёртка над «действием оплаты»: телефон есть — выполняем сразу, нет —
 *  спрашиваем и выполняем после сохранения.
 *
 *  Использование:
 *      const phoneGate = usePhoneGate();
 *      <Button onClick={() => phoneGate.run(() => startCheckout(slug))} />
 *      {phoneGate.modal}
 */
export function usePhoneGate() {
  const { user } = useAuth();
  // Открыта ли модалка — состояние (по нему перерисовываемся). Само отложенное
  // действие лежит в ref: «забрать и выполнить» обязано быть одним движением.
  // Из состояния его читало бы замыкание перерисовки, и второй onSaved из того же
  // замыкания увидел бы то же действие — и выполнил бы его ещё раз.
  const [asking, setAsking] = useState(false);
  const pendingRef = useRef(null);
  const needsPhone = !!(user && !(user.phone || '').trim());

  const run = useCallback((action) => {
    if (!needsPhone) return action();
    pendingRef.current = action;
    setAsking(true);
    return undefined;
  }, [needsPhone]);

  // Забрать отложенное действие и закрыть модалку; после этого ждать нечего.
  // Закрытие без сохранения — тот же шаг, только взятое не выполняют. Поэтому шаг,
  // закрытый посреди сохранения (крестик, Esc, фон), действие отменяет: номер
  // сохранится, а пространство или платёж вдогонку закрытому окну не появятся.
  const take = () => {
    const fn = pendingRef.current;
    pendingRef.current = null;
    setAsking(false);
    return fn;
  };

  const modal = (
    <PhoneGateModal
      open={asking}
      onClose={take}
      onSaved={() => { const fn = take(); if (fn) fn(); }} />
  );

  return { needsPhone, run, modal };
}
